// path: src/utils/runConfig.js

import { getStoryLevelById } from "../data/storyLevels";

const DEFAULT_TUTORIAL_BEHAVIOR = {
    forgivingMistakes: false,
    twistedCardLimit: 0,
    maxHiddenCards: null,
    shyStartDelayMs: null,
    hardcorePreview: false,
    guidedTutorial: false,
    // Show the heart-continue prompt but do not deduct (Story teaching).
    houseHeartContinue: false,
};

const VALID_BOARD_SIZES = [4, 9, 16];

export function normalizeTutorialBehavior(behavior) {
    if (!behavior || typeof behavior !== "object") {
        return { ...DEFAULT_TUTORIAL_BEHAVIOR };
    }

    return {
        forgivingMistakes: Boolean(behavior.forgivingMistakes),
        twistedCardLimit: Number.isFinite(Number(behavior.twistedCardLimit))
            ? Math.max(0, Number(behavior.twistedCardLimit))
            : 0,
        maxHiddenCards:
            behavior.maxHiddenCards === null ||
            behavior.maxHiddenCards === undefined
                ? null
                : Math.max(0, Number(behavior.maxHiddenCards)),
        shyStartDelayMs:
            behavior.shyStartDelayMs === null ||
            behavior.shyStartDelayMs === undefined
                ? null
                : Math.max(0, Number(behavior.shyStartDelayMs)),
        hardcorePreview: Boolean(behavior.hardcorePreview),
        guidedTutorial: Boolean(behavior.guidedTutorial),
        houseHeartContinue: Boolean(behavior.houseHeartContinue),
    };
}

function normalizeClearTarget(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 1;
}

function normalizeScoreExcludedModifiers(value) {
    const source = value && typeof value === "object" ? value : {};
    return {
        shyCorgisMode: Boolean(source.shyCorgisMode),
        anxiousMode: Boolean(source.anxiousMode),
    };
}

export function normalizeRunConfig(input = {}) {
    const runType = input.runType === "story" ? "story" : "endless";
    let boardSize = Number(input.boardSize);

    if (!VALID_BOARD_SIZES.includes(boardSize)) {
        boardSize = 4;
    }

    const blitzMode = Boolean(input.blitzMode);
    const hardcoreMode = blitzMode ? true : Boolean(input.hardcoreMode);

    return {
        runType,
        storyLevelId:
            runType === "story" && Number.isFinite(Number(input.storyLevelId))
                ? Number(input.storyLevelId)
                : null,
        boardSize,
        clearTarget:
            runType === "story" ? normalizeClearTarget(input.clearTarget) : 1,
        requirePerfectClears:
            runType === "story" ? Boolean(input.requirePerfectClears) : false,
        objectiveSummary:
            runType === "story" && typeof input.objectiveSummary === "string"
                ? input.objectiveSummary
                : null,
        twistedMode: Boolean(input.twistedMode),
        shyCorgisMode: Boolean(input.shyCorgisMode),
        anxiousMode: Boolean(input.anxiousMode),
        hardcoreMode,
        blitzMode,
        scoreExcludedModifiers: normalizeScoreExcludedModifiers(
            input.scoreExcludedModifiers
        ),
        tutorialBehavior:
            runType === "story"
                ? normalizeTutorialBehavior(input.tutorialBehavior)
                : null,
    };
}

export function buildStoryRunConfig(storyLevelId) {
    const level = getStoryLevelById(storyLevelId);

    if (!level) {
        return null;
    }

    return normalizeRunConfig({
        runType: "story",
        storyLevelId: level.id,
        boardSize: level.boardSize,
        clearTarget: level.clearTarget,
        requirePerfectClears: level.requirePerfectClears,
        objectiveSummary: level.objectiveSummary,
        twistedMode: level.modifiers.twistedMode,
        shyCorgisMode: level.modifiers.shyCorgisMode,
        anxiousMode: Boolean(level.modifiers.anxiousMode),
        hardcoreMode: level.modifiers.hardcoreMode,
        blitzMode: level.modifiers.blitzMode,
        tutorialBehavior: level.tutorialBehavior,
    });
}

export function buildEndlessRunConfig({
    boardSize = 4,
    twistedMode = false,
    shyCorgisMode = false,
    anxiousMode = false,
    hardcoreMode = false,
    blitzMode = false,
} = {}) {
    return normalizeRunConfig({
        runType: "endless",
        storyLevelId: null,
        boardSize,
        twistedMode,
        shyCorgisMode,
        anxiousMode,
        hardcoreMode,
        blitzMode,
        tutorialBehavior: null,
    });
}

/**
 * Post-story Hard Mode: every stage is Hardcore. Tutorial forgiveness is off.
 */
export function applyStoryHardMode(runConfig) {
    if (!isStoryRun(runConfig)) {
        return runConfig;
    }

    const behavior = runConfig.tutorialBehavior || {};

    return normalizeRunConfig({
        ...runConfig,
        hardcoreMode: true,
        tutorialBehavior: {
            ...behavior,
            forgivingMistakes: false,
            guidedTutorial: false,
            houseHeartContinue: false,
        },
    });
}

/**
 * Legacy difficulty picker adapter used during migration / admin.
 */
export function buildLegacyDifficultyRunConfig(
    mode,
    options = {},
    scoreAtStartOfLevelFn
) {
    let startLevel = 1;

    if (mode === "challenging") {
        startLevel = 4;
    }

    if (mode === "hard") {
        startLevel = 7;
    }

    if (mode === "blitz") {
        startLevel = 1;
    }

    const startScore =
        typeof scoreAtStartOfLevelFn === "function"
            ? scoreAtStartOfLevelFn(startLevel)
            : 0;

    const boardSize =
        startLevel >= 7 ? 16 : startLevel >= 4 ? 9 : 4;

    return {
        ...normalizeRunConfig({
            runType: "endless",
            boardSize,
            twistedMode: Boolean(options.twistedMode),
            shyCorgisMode: Boolean(options.shyCorgisMode),
            anxiousMode: Boolean(options.anxiousMode),
            hardcoreMode:
                mode === "blitz" ? true : Boolean(options.hardcoreMode),
            blitzMode: mode === "blitz",
        }),
        // Temporary legacy fields for App.goPlay adapter
        legacyStartLevel: startLevel,
        legacyStartScore: startScore,
    };
}

export function isStoryRun(runConfig) {
    return Boolean(runConfig && runConfig.runType === "story");
}

export function isEndlessRun(runConfig) {
    return Boolean(runConfig && runConfig.runType === "endless");
}

export function getStoryClearTarget(runConfig) {
    if (!isStoryRun(runConfig)) {
        return null;
    }

    return normalizeClearTarget(runConfig.clearTarget);
}

export function getStoryCompletionThreshold(runConfig) {
    if (!isStoryRun(runConfig)) {
        return null;
    }

    return runConfig.boardSize * getStoryClearTarget(runConfig);
}

export function getEndlessLevelThreshold(boardSize, levelIndex) {
    const size = VALID_BOARD_SIZES.includes(Number(boardSize))
        ? Number(boardSize)
        : 16;
    const index = Math.max(0, Number(levelIndex) || 0);

    return size * (index + 1);
}
